<template>
  <div
    class="app-layout app-layout-backend"
    :class="{ 'app-layout-backend--full': hideFooter }"
  >
    <div v-if="sidebarOpen" class="admin-sidebar-backdrop d-md-none" @click="sidebarOpen = false"></div>
    <app-sidebar
      :class="{ 'sidebar-open': sidebarOpen }"
      @navigate="sidebarOpen = false"
      @collapse-change="sidebarCollapsed = $event"
    />
    <div
      class="backend-main flex-grow-1 min-vw-0 d-flex flex-column"
      :class="{ 'backend-main--sidebar-collapsed': sidebarCollapsed }"
    >
      <div class="admin-topbar-shell" :class="{ 'admin-topbar-shell-dark': isDarkMode }">
        <admin-topbar
          :search="topbarSearch"
          :search-placeholder="topbarSearchPlaceholder"
          :show-search="showTopbarSearch"
          :show-dashboard-geo-filters="isDashboardRoute"
          :dashboard-primary-options="dashboardGeoPrimaryOptions"
          :dashboard-secondary-options="dashboardGeoSecondaryOptions"
          :dashboard-primary-selected="dashboardGeoPrimarySelected"
          :dashboard-secondary-selected="dashboardGeoSecondarySelected"
          :show-date-filters="showTopbarDateFilters"
          :show-report-actions="isReportRoute"
          :show-clear-button="showTopbarClearButton"
          :show-select-filter="showTopbarSelectFilter"
          :show-search-category="showTopbarSearchCategory"
          :search-category-options="topbarSearchCategoryOptions"
          :search-category-selected="topbarSearchKey"
          :search-category-fallback-label="topbarSearchCategoryFallbackLabel"
          :filter-options="topbarFilterOptions"
          :filter-selected="topbarDropdownSelected"
          :filter-fallback-label="topbarFilterFallbackLabel"
          :start-date="dashboardStartDate"
          :end-date="dashboardEndDate"
          :start-date-fallback-label="topbarStartDateFallbackLabel"
          :end-date-fallback-label="topbarEndDateFallbackLabel"
          :min-date="topbarMinDate"
          :max-date="topbarMaxDate"
          :date-filters-placement="topbarDateFiltersPlacement"
          :reverse-date-order="isComplaintTrackerRoute || isCxPulse360Route"
          :single-date-filter="isPopRoute"
          :show-month-year-filters="showTopbarMonthYearFilters"
          :month-options="topbarMonthOptions"
          :year-options="topbarYearOptions"
          :month-selected="topbarSelectedMonth"
          :year-selected="topbarSelectedYear"
          :is-dark-mode="isDarkMode"
          @update:search="onTopbarSearchInput"
          @update:searchCategorySelected="onTopbarSearchCategoryChange"
          @update:filterSelected="onTopbarFilterChange"
          @update:monthSelected="onTopbarMonthChange"
          @update:yearSelected="onTopbarYearChange"
          @update:dashboardPrimarySelected="onDashboardGeoPrimaryChange"
          @update:dashboardSecondarySelected="onDashboardGeoSecondaryChange"
          @update:startDate="onTopbarStartDateChange"
          @update:endDate="onTopbarEndDateChange"
          @apply-filter="onDashboardApplyFilter"
          @download-report="onOsaReportDownload"
          @download-mis="onOsaDownloadMis"
          @clear-dates="onTopbarClearDates"
          @toggle-dark="toggleDarkMode"
          @toggle-sidebar="sidebarOpen = !sidebarOpen"
        />
      </div>
      <b-container
        :class="[
          'backend-content flex-grow-1',
          hideFooter ? '' : isDarkMode ? 'backend-content-dark text-light' : 'text-dark',
          { 'app-layout-backend__container--full': hideFooter }
        ]"
        fluid
      >
        <div class="admin-page-shell" :class="{ 'admin-page-shell--full': hideFooter }">
          <slot />
        </div>
        <footer-bar v-if="!hideFooter" />
      </b-container>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import AppSidebar from '@/components/AppSidebar.vue';
import AdminTopbar from '@/components/AdminTopbar.vue';
import FooterBar from '@/components/FooterBar.vue';
import { Months } from '@/helper/pettyCashHelper';
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'BackendLayout',
  data() {
    return {
      sidebarOpen: false,
      sidebarCollapsed: false,
      topbarSearch: '',
      topbarFilterSelected: null,
      topbarSearchKey: 'CheckList Name',
      dashboardStartDate: moment().format('YYYY-MM-DD'),
      dashboardEndDate: moment().format('YYYY-MM-DD'),
      dashboardMinDate: moment('2020-01-01').format('YYYY-MM-DD'),
      dashboardMaxDate: moment().format('YYYY-MM-DD'),
      dashboardGeoPrimaryOptions: [{ text: 'select', value: 'select' }],
      dashboardGeoSecondaryOptions: [{ text: 'select', value: 'select' }],
      dashboardGeoPrimarySelected: 'select',
      dashboardGeoSecondarySelected: 'select',
      topbarSelectedMonth: moment().format('MMMM').toUpperCase(),
      topbarSelectedYear: new Date().getFullYear(),
      // Prefill/clear dates are UI-only until the user changes them or Apply commits them.
      topbarDatesDraft: true,
      formOperationsSearchCategoryOptions: ['Select Filter']
    };
  },
  computed: {
    ...mapState('darkMode', ['isDarkMode']),
    ...mapState('barcode', ['classList']),
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('pettyCash', ['ifAdmin', 'ifSpencerAdmin']),
    hideFooter() {
      return this.$route.meta.hideFooter === true;
    },
    isDashboardRoute() {
      return this.$route.name === 'dashboard';
    },
    isChecklistRoute() {
      const name = this.$route.name || '';
      return (
        String(name).includes('checklist') ||
        name === 'fill-checklist' ||
        name === 'draft-checklists' ||
        name === 'report'
      );
    },
    isReportRoute() {
      const name = this.$route.name || '';
      return name === 'report' || name === 'reportPage';
    },
    isEventsRoute() {
      return this.$route.name === 'events';
    },
    isEventPlanListRoute() {
      return this.$route.name === 'eventplanlist';
    },
    isEventDetailRoute() {
     return this.$route.name === 'events-view';
    },
    isFoodSafetyReportsRoute() {
      return this.$route.name === 'fodSafety_Reports';
    },
    isNpsVendorRoute() {
      return this.$route.name === 'vendor-feedback-report';
    },
    isNpsVocRoute() {
      return this.$route.name === 'nps';
    },
    isNpsViewRoute() {
      return this.$route.name === 'nps-view';
    },
    isChecklistListRoute() {
      return this.$route.name === 'checklist';
    },
    isFormListRoute() {
      return this.$route.name === 'form';
    },
    isFormOperationsRoute() {
      return this.$route.name === 'form-operations';
    },
    isFormCreateRoute() {
      return this.$route.name === 'form-new';
    },
    isFormFillRoute() {
      return this.$route.name === 'form-fill';
    },
    isEventPlanCreateRoute() {
      return this.$route.name === 'event-plan-new' || this.$route.name === 'event-plan-view';
    },
    isPettyCashHubRoute() {
      return this.$route.name === 'petty-cash';
    },
    isPettyCashBudgetRoute() {
      return this.$route.name === 'petty-cash-budget';
    },
    isPettyCashBudgetIncreaseRoute() {
      return this.$route.name === 'petty-cash-budgetIncrease-request';
    },
    isPettyCashBudgetSummaryRoute() {
      return this.$route.name === 'petty-cash-BudgetSummary';
    },
    isPettyCashBudgetFormRoute() {
      const name = this.$route.name || '';
      return name === 'petty-cash-budget-new' || name === 'petty-cash-budget-edit';
    },
    isPettyCashExpenseFormRoute() {
      const name = this.$route.name || '';
      return name === 'petty-cash-expense-new' || name === 'petty-cash-expense-edit';
    },
    isPettyCashExpenseSettingFormRoute() {
      const name = this.$route.name || '';
      return name === 'petty-cash-expense-setting-new' || name === 'petty-cash-expense-setting-edit';
    },
    isPettyCashExpenseSettingRoute() {
      return this.$route.name === 'petty-cash-expense-setting';
    },
    isPettyCashAccountRoute() {
      return this.$route.name === 'petty-cash-account';
    },
    isPettyCashAccountFormRoute() {
      return this.$route.name === 'petty-cash-account-new';
    },
    isPettyCashAccountEditRoute() {
      return this.$route.name === 'petty-cash-account-edit';
    },
    showPettyCashAccountEditTopbarFilters() {
      return this.isPettyCashAccountEditRoute;
    },
    isPettyCashExpenseRoute() {
      return this.$route.name === 'petty-cash-expense';
    },
    isPettyCashCertificateDashboardRoute() {
      return this.$route.name === 'dashboard-list';
    },
    isPettyCashCcListRoute() {
      return this.$route.name === 'cc-list';
    },
    isPettyCashCashCertificateListRoute() {
      return this.$route.name === 'petty-cash-certificate-list';
    },
    isPettyCashCcFormRoute() {
      return this.$route.name === 'cc-form-new' || this.$route.name === 'cc-form-update';
    },
    isUserListRoute() {
      return this.$route.name === 'user';
    },
    isUserFormRoute() {
      return this.$route.name === 'user-new' || this.$route.name === 'user-edit';
    },
    isRoleListRoute() {
      return this.$route.name === 'role';
    },
    isRoleFormRoute() {
      return this.$route.name === 'role-new' || this.$route.name === 'role-edit';
    },
    isStoreListRoute() {
      return this.$route.name === 'stores';
    },
    isStoreFormRoute() {
      return this.$route.name === 'store-new' || this.$route.name === 'store-edit';
    },
    isQmsListRoute() {
      return this.$route.name === 'qms' || this.$route.name === 'qms-admin';
    },
    isQmsFormRoute() {
      return ['qms-new', 'qms-new-direct', 'qms-edit'].includes(this.$route.name);
    },
    isEscalationMatrixRoute() {
      return ['escalation-matrix', 'escalation-matrix-new', 'escalation-matrix-edit'].includes(this.$route.name);
    },
    isDropdownAdminRoute() {
      return ['dropdown-admin', 'dropdown-admin-new', 'dropdown-admin-edit'].includes(this.$route.name);
    },
    isClosureRemarksRoute() {
      return ['qms-closure-remarks', 'qms-closure-remarks-new', 'qms-closure-remarks-edit'].includes(
        this.$route.name
      );
    },
    isComplaintTrackerRoute() {
      return this.$route.name === 'complain';
    },
    isPopRoute() {
      return this.$route.name === 'pop';
    },
    isPopManualRoute() {
      return this.$route.name === 'popmanual';
    },
    isCxPulse360Route() {
      return this.$route.name === 'cx-pulse-360';
    },
    isSamparkRoute() {
      return this.$route.name === 'kyc';
    },
    isSharedListTopbarRoute() {
      return ['transaction-complain', 'ene-tracker', 'rtv', 'schedule', 'document-share', 'kyc-institutional'].includes(
        this.$route.name
      );
    },
    isAdditionalListTopbarRoute() {
      return [
        'license-upload',
        'promo-code',
        'gift-distribute-app',
        'notes',
        'tags',
        'video'
        // 'petty-cash-expense-setting' — topbar search/filter/dates hidden on this page
      ].includes(this.$route.name);
    },
    isBarcodeScanRoute() {
      return ['barcode-scan', 'barcode-category-list'].includes(this.$route.name);
    },
    isTopbarCurrentDatePrefillRoute() {
      // "Today" is shown in the topbar for these routes, but stays draft
      // (UI-only) until the user changes a date or clicks Apply Filter.
      return (
        this.isReportRoute ||
        this.isChecklistListRoute ||
        this.isEventsRoute ||
        this.isNpsVocRoute ||
        this.isComplaintTrackerRoute ||
        this.isPopRoute ||
        this.isSamparkRoute ||
        this.isBarcodeScanRoute ||
        this.isSharedListTopbarRoute ||
        this.isAdditionalListTopbarRoute ||
        this.isCxPulse360Route ||
        this.isFoodSafetyReportsRoute ||
        this.isQmsListRoute ||
        this.isEventPlanListRoute ||
        this.$route.name === 'schedule' ||
        this.$route.name === 'document-share' ||
        this.$route.name === 'kyc-institutional'
      );
    },
    showPettyCashAccountTopbarFilters() {
      return this.isPettyCashAccountRoute && !this.ifAdmin && !this.ifSpencerAdmin;
    },
    isChecklistFormRoute() {
      const name = this.$route.name || '';
      return (
        name === 'checklist-new' ||
        name === 'checklist-edit' ||
        name === 'fill-checklist' ||
        name === 'checklist-fill' ||
        name === 'draft-checklists' ||
        name === 'report-view'
      );
    },
    isKycFormRoute() {
      const name = this.$route.name || '';
      return name === 'kyc-new';
    },
    isTransactionComplainFormRoute() {
      const name = this.$route.name || '';
      return name === 'transaction-complain-new' || name === 'complain-view';
    },
    isEnETrackerFormRoute() {
      const name = this.$route.name || '';
      return name === 'ene-tracker-new' || name === 'ene-tracker-edit';
    },
    isTmsFormRoute() {
      const name = this.$route.name || '';
      return name === 'tms-new' || name === 'tms-edit';
    },
    isRtvFormRoute() { 
      const name = this.$route.name || '';
      return name === 'rtv-new' || name === 'rtv-edit';
    },
    isCxPulse360FormRoute() {
      const name = this.$route.name || '';
      return name === 'cx-pulse-360-new' || name === 'cx-pulse-360-edit';
    },
    showTopbarSearch() {
      if (this.isClosureRemarksRoute) {
        return false;
      }
      if (this.isPettyCashAccountFormRoute || this.isPettyCashAccountEditRoute) {
        return false;
      }
      if (this.isPettyCashAccountRoute && !this.showPettyCashAccountTopbarFilters) {
        return false;
      }
      if (this.isPettyCashExpenseSettingRoute) {
        return false;
      }
      if (this.isPopManualRoute) {
        return false;
      }

      return (
        !this.isDashboardRoute &&
        !this.isChecklistFormRoute &&
        !this.isNpsViewRoute &&
        !this.isKycFormRoute &&
        !this.isTransactionComplainFormRoute &&
        !this.isFormListRoute &&
        !this.isFormCreateRoute &&
         !this.isFormFillRoute &&
        !this.isEventPlanCreateRoute &&
        !this.isPettyCashHubRoute &&
        !this.isPettyCashCertificateDashboardRoute &&
        !this.isPettyCashCcListRoute &&
        !this.isPettyCashCashCertificateListRoute &&
        !this.isPettyCashCcFormRoute &&
        !this.isPettyCashBudgetFormRoute &&
        !this.isPettyCashBudgetSummaryRoute &&
        !this.isPettyCashExpenseFormRoute &&
        !this.isPettyCashExpenseSettingFormRoute &&
        !this.isPettyCashAccountFormRoute &&
        !this.isUserFormRoute &&
        !this.isRoleListRoute &&
        !this.isRoleFormRoute &&
        !this.isStoreListRoute &&
        !this.isStoreFormRoute &&
        !this.isQmsFormRoute &&
        !this.isEscalationMatrixRoute &&
        !this.isDropdownAdminRoute &&
        !this.isClosureRemarksRoute &&
        !this.isBarcodeScanRoute &&
        !this.isCxPulse360FormRoute &&
        !this.isEnETrackerFormRoute &&
        !this.isTmsFormRoute &&
        !this.isRtvFormRoute &&
        !this.isEventDetailRoute &&
        ![
          'video-new',
          'video-edit',
          'document-new',
          'document-edit',
          'promo-code-new',
          'promo-code-edit',
          'license-new',
          'license-view',
          'tags-new',
          'tags-edit',
          'notes-new',
          'notes-view',
          'comments',
          'contact-us'
        ].includes(
          this.$route.name
        )
      );
    },
    showTopbarDateFilters() {
      if (this.isChecklistFormRoute) {
        return false;
      }
      return (
        this.isDashboardRoute ||
        this.isChecklistRoute ||
        this.isReportRoute ||
        this.isEventsRoute ||
        this.isEventPlanListRoute ||
        this.isFoodSafetyReportsRoute ||
        this.isNpsVendorRoute ||
        this.isNpsVocRoute ||
        this.isFormListRoute ||
        this.isFormOperationsRoute ||
        this.isPettyCashExpenseRoute ||
        this.showPettyCashAccountEditTopbarFilters ||
        this.isComplaintTrackerRoute ||
        this.isPopRoute ||
        this.isCxPulse360Route ||
        this.isSamparkRoute ||
        this.isQmsListRoute ||
        this.isSharedListTopbarRoute ||
        this.isAdditionalListTopbarRoute
        || this.isBarcodeScanRoute
      );
    },
    showTopbarClearButton() {
      if (this.isChecklistFormRoute) {
        return false;
      }
      return (
        this.isReportRoute ||
        this.isChecklistRoute ||
        this.isEventsRoute ||
        this.isEventPlanListRoute ||
        this.isNpsVendorRoute ||
        this.isNpsVocRoute ||
        this.isFormListRoute ||
        this.isFormOperationsRoute ||
        this.isUserListRoute ||
        this.isPettyCashBudgetRoute ||
        this.isPettyCashBudgetIncreaseRoute ||
        this.showPettyCashAccountTopbarFilters ||
        this.showPettyCashAccountEditTopbarFilters ||
        this.isPettyCashExpenseRoute ||
        this.isComplaintTrackerRoute ||
        this.isPopRoute ||
        this.isCxPulse360Route ||
        this.isSamparkRoute ||
        this.isQmsListRoute ||
        this.isSharedListTopbarRoute ||
        this.isAdditionalListTopbarRoute
        || this.isBarcodeScanRoute
      );
    },
    showTopbarMonthYearFilters() {
      return (
        this.isPettyCashBudgetRoute ||
        this.isPettyCashBudgetIncreaseRoute ||
        this.showPettyCashAccountTopbarFilters
      );
    },
    topbarMonthOptions() {
      return Months;
    },
    topbarYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear - 2; year <= currentYear + 2; year += 1) {
        years.push(year);
      }
      return years;
    },
    topbarMinDate() {
      if (
        this.isFormListRoute ||
        this.isFormOperationsRoute ||
        this.isPettyCashExpenseRoute ||
        this.showPettyCashAccountEditTopbarFilters ||
        this.isComplaintTrackerRoute ||
        this.isPopRoute ||
        this.isCxPulse360Route ||
        this.isSamparkRoute ||
        this.isQmsListRoute ||
        this.isAdditionalListTopbarRoute ||
        this.isBarcodeScanRoute
      ) {
        return moment('2020-01-01').format('YYYY-MM-DD');
      }
      return this.dashboardMinDate;
    },
    topbarMaxDate() {
      if (this.isPettyCashExpenseRoute || this.showPettyCashAccountEditTopbarFilters || this.isComplaintTrackerRoute || this.isPopRoute || this.isCxPulse360Route || this.isSamparkRoute || this.isAdditionalListTopbarRoute || this.isBarcodeScanRoute || this.isFormOperationsRoute) {
        return moment().format('YYYY-MM-DD');
      }
      return this.dashboardMaxDate;
    },
    topbarStartDateFallbackLabel() {
      return 'Start Date';
    },
    topbarEndDateFallbackLabel() {
      return 'End Date';
    },
    topbarDateFiltersPlacement() {
      if (this.isFormListRoute || this.isBarcodeScanRoute) {
        return 'actions';
      }
      return 'auto';
    },
    showTopbarSelectFilter() {
      return (
        this.isQmsListRoute ||
        this.isPettyCashBudgetIncreaseRoute ||
        this.showPettyCashAccountEditTopbarFilters ||
        this.isEventsRoute ||
        this.isFoodSafetyReportsRoute ||
        this.isNpsVendorRoute ||
        (this.isSharedListTopbarRoute &&
          ![
            'transaction-complain',
            'ene-tracker',
            'rtv',
            'schedule',
            'document-share',
            'kyc-institutional'
          ].includes(this.$route.name))
      );
    },
    showTopbarSearchCategory() {
      return (
        this.isReportRoute ||
        this.isFoodSafetyReportsRoute ||
        this.isChecklistListRoute ||
        this.isEventPlanListRoute ||
        this.isNpsVocRoute ||
        this.isPettyCashBudgetRoute ||
        this.isPettyCashBudgetIncreaseRoute ||
        this.showPettyCashAccountTopbarFilters ||
        this.isPettyCashExpenseRoute ||
        this.isComplaintTrackerRoute ||
        this.isPopRoute ||
        this.isCxPulse360Route ||
        this.isSamparkRoute ||
        this.isQmsListRoute ||
        this.isSharedListTopbarRoute ||
        this.isAdditionalListTopbarRoute ||
        this.isFormOperationsRoute ||
        this.isUserListRoute
      );
    },
    topbarSearchCategoryOptions() {
      if (this.isUserListRoute) {
        return ['Name', 'Mobile', 'Role'];
      }
      if (this.isQmsListRoute) {
        if (this.isQmsOtpGuestSession) {
          return ['Query Type'];
        }
        return ['Employee Mobile', 'Employee Code', 'Store Id', 'Employee Name', 'Department', 'Query Type'];
      }
      if (this.isFormOperationsRoute) {
        return this.formOperationsSearchCategoryOptions.length
          ? this.formOperationsSearchCategoryOptions
          : ['Select Filter'];
      }
      if (this.isCxPulse360Route) {
        return ['Store Id', 'Customer Mobile'];
      }
      if (this.isSamparkRoute) {
        return ['Customer Name', 'Mobile'];
      }
      if (this.$route.name === 'kyc-institutional') {
        return ['Customer Name', 'Mobile'];
      }
      if (this.$route.name === 'license-upload') {
        return ['Store ID', 'File Name'];
      }
      if (this.$route.name === 'promo-code') {
        return ['Promo Id'];
      }
      if (this.$route.name === 'gift-distribute-app') {
        return ['Bill Number'];
      }
      if (this.$route.name === 'notes') {
        return ['Store Id', 'Department', 'Sub Department', 'ASM Name'];
      }
      if (this.$route.name === 'tags') {
        return ['Tag Category', 'Tag'];
      }
      if (this.$route.name === 'video') {
        return ['Video Title'];
      }
      // if (this.$route.name === 'petty-cash-expense-setting') {
      //   return ['Company'];
      // }
      if (this.isBarcodeScanRoute) {
        return [{ value: null, text: 'Select Filter' }].concat(
          (this.classList || []).map(item => ({ value: item.class, text: item.class }))
        );
      }
      if (this.$route.name === 'schedule') {
        return ['ASM Name'];
      }
      if (this.$route.name === 'document-share') {
        return ['Document Title', 'Store Id'];
      }
      if (['transaction-complain', 'ene-tracker', 'rtv'].includes(this.$route.name)) {
        return ['Name', 'Store ID', 'Store Name', 'Transaction ID', 'Transaction Date'];
      }
      if (this.isPopRoute) {
        return ['Store Id', 'Article Code'];
      }
      if (this.isComplaintTrackerRoute) {
        return ['Complain Number', 'Store Id'];
      }
      if (this.isPettyCashExpenseRoute) {
        return ['Store Id', 'Gl Name', 'Expense Gl', 'Company Name'];
      }
      if (this.showPettyCashAccountTopbarFilters) {
        return ['Store Id'];
      }
      if (this.isPettyCashBudgetRoute) {
        return ['Store Id', 'Store Type', 'Company'];
      }
      if (this.isPettyCashBudgetIncreaseRoute) {
        return ['Store Id'];
      }
      if (this.isChecklistListRoute || this.isEventPlanListRoute) {
        return ['Name', 'Store Id'];
      }
      if (this.isFoodSafetyReportsRoute) {
        return ['CheckList Name', 'Store Id', 'Store Name'];
      }
      if (this.isReportRoute) {
        return ['CheckList Name', 'Store Id'];
      }
      if (this.isNpsVocRoute) {
        return ['Mobile', 'Store Id'];
      }
      return [];
    },
    topbarSearchCategoryFallbackLabel() {
      if (this.isUserListRoute) {
        return 'Name';
      }
      if (this.isQmsListRoute) {
        return this.isQmsOtpGuestSession ? 'Query Type' : 'Employee Mobile';
      }
      if (this.isFormOperationsRoute) {
        return 'Select Filter';
      }
      if (this.isCxPulse360Route) {
        return 'Store Id';
      }
      if (this.isSamparkRoute) {
        return 'Customer Name';
      }
      if (this.$route.name === 'kyc-institutional') {
        return 'Customer Name';
      }
      if (this.$route.name === 'license-upload') {
        return 'Store ID';
      }
      if (this.$route.name === 'promo-code') {
        return 'Promo Id';
      }
      if (this.$route.name === 'gift-distribute-app') {
        return 'Bill Number';
      }
      if (this.$route.name === 'notes') {
        return 'Store Id';
      }
      if (this.$route.name === 'tags') {
        return 'Tag Category';
      }
      if (this.$route.name === 'video') {
        return 'Video Title';
      }
      // if (this.$route.name === 'petty-cash-expense-setting') {
      //   return 'Company';
      // }
      if (this.$route.name === 'schedule') {
        return 'ASM Name';
      }
      if (this.$route.name === 'document-share') {
        return 'Document Title';
      }
      if (['transaction-complain', 'ene-tracker', 'rtv'].includes(this.$route.name)) {
        return 'Name';
      }
      if (this.isPopRoute) {
        return 'Store Id';
      }
      if (this.isPettyCashBudgetRoute || this.showPettyCashAccountTopbarFilters) {
        return 'Store Id';
      }
      if (this.isPettyCashBudgetIncreaseRoute) {
        return 'Store Id';
      }
      if (this.isComplaintTrackerRoute) {
        return 'Complain Number';
      }
      if (this.isPettyCashExpenseRoute) {
        return 'Store Id';
      }
      if (this.isChecklistListRoute || this.isEventPlanListRoute) {
        return 'Name';
      }
      if (this.isNpsVocRoute) {
        return 'Mobile';
      }
      return 'CheckList Name';
    },
    topbarFilterOptions() {
      if (this.isQmsListRoute) {
        return [
          { value: null, text: 'Select Status' },
          { value: 'open', text: 'Open' },
          { value: 'closed', text: 'Closed' }
        ];
      }
      if (this.isChecklistListRoute) {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Active-True', text: 'Active-True' },
          { value: 'Active-False', text: 'Active-False' }
        ];
      }
      if (this.isFoodSafetyReportsRoute) {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'DRAFT', text: 'DRAFT' },
          { value: 'REVIEW', text: 'REVIEW' },
          { value: 'RELEASED', text: 'RELEASED' },
          { value: 'CLOSED', text: 'CLOSED' }
        ];
      }
      if (this.isReportRoute) {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Completed-True', text: 'Completed-True' },
          { value: 'Completed-False', text: 'Completed-False' }
        ];
      }
      if (this.$route.name === 'license-upload') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Expired-True', text: 'Expired-True' },
          { value: 'Expired-False', text: 'Expired-False' }
        ];
      }
      if (this.$route.name === 'video') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Active-True', text: 'Active-True' },
          { value: 'Active-False', text: 'Active-False' }
        ];
      }
      if (this.isEventsRoute) {
        return [
          { value: 'Store Id', text: 'Store ID' },
          { value: 'Event Name', text: 'Event Name' }
        ];
      }
      if (this.isNpsVendorRoute) {
        return [
          { value: 'Vendor Code', text: 'Vendor Code' },
          { value: 'Vendor Name', text: 'Vendor Name' }
        ];
      }
      if (this.$route.name === 'transaction-complain') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'CREATED', text: 'CREATED' },
          { value: 'CLOSED', text: 'CLOSED' },
          { value: 'IN PROGRESS', text: 'IN PROGRESS' }
        ];
      }
      if (this.$route.name === 'ene-tracker') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'PENDING', text: 'PENDING' },
          { value: 'RESOLVED', text: 'RESOLVED' },
          { value: 'NOT_RESOLVED', text: 'NOT RESOLVED' }
        ];
      }
      if (this.$route.name === 'schedule') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Schedule_Type-WEEK_OFF', text: 'Schedule Type-WEEK OFF' },
          { value: 'Schedule_Type-LEAVE', text: 'Schedule Type-LEAVE' },
          { value: 'Schedule_Type-ROHO', text: 'Schedule Type-RO/HO' },
          { value: 'Schedule_Type-STORE_VISIT', text: 'Schedule Type-Store Visit' }
        ];
      }
      if (this.$route.name === 'document-share') {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Active-True', text: 'Active-True' },
          { value: 'Active-False', text: 'Active-False' },
          { value: 'Type-SOP', text: 'Type-SOP' },
          { value: 'Type-PLANOGRAM', text: 'Type-PLANOGRAM' },
          { value: 'Type-LAYOUT', text: 'Type-LAYOUT' },
          { value: 'Type-C&B', text: 'Type-C&B' },
          { value: 'Type-LIQUOR_GRN', text: 'Type-LIQUOR_GRN' }
        ];
      }
      if (this.showPettyCashAccountTopbarFilters) {
        return [
          { value: null, text: 'Select Type' },
          { value: 'CREDIT', text: 'CREDIT' },
          { value: 'DEBIT', text: 'DEBIT' }
        ];
      }
      if (this.showPettyCashAccountEditTopbarFilters) {
        return [
          { value: null, text: 'Select Type' },
          { value: 'CREDIT', text: 'CREDIT' },
          { value: 'DEBIT', text: 'DEBIT' }
        ];
      }
      if (this.isPettyCashBudgetIncreaseRoute) {
        return [
          { value: null, text: 'Select Filter' },
          { value: 'Pending', text: 'PENDING' },
          { value: 'Approved', text: 'APPROVED' },
          { value: 'Rejected', text: 'REJECTED' }
        ];
      }
      return [];
    },
    topbarFilterFallbackLabel() {
      if (this.isQmsListRoute) {
        return 'Select Status';
      }
      if (this.isEventsRoute) {
        return 'Store ID';
      }
      if (this.isNpsVendorRoute) {
        return 'Vendor Code';
      }
      if (this.showPettyCashAccountTopbarFilters) {
        return 'Select Type';
      }
      if (this.showPettyCashAccountEditTopbarFilters) {
        return 'Select Type';
      }
      return 'Select Filter';
    },
    topbarDropdownSelected() {
      if (this.isEventsRoute || this.isNpsVendorRoute) {
        return this.topbarSearchKey;
      }
      return this.topbarFilterSelected;
    },
    topbarSearchPlaceholder() {
      return 'Search...';
    },
    isQmsOtpGuestSession() {
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      return sessionStorage.getItem('qmsOtpVerified') === 'true' && !this.isLoggedIn();
    }
  },
  watch: {
    '$route.fullPath': function onRouteFullPathChange() {
      this.sidebarOpen = false;
      this.syncTopbarFromRoute();
    }
  },
  mounted() {
    this.$root.$on('dashboard-dates-init', this.onDashboardDatesInit);
    this.$root.$on('dashboard-geo-filters-update', this.onDashboardGeoFiltersUpdate);
    this.$root.$on('form-operations-search-options', this.onFormOperationsSearchOptions);
    this.syncTopbarFromRoute();
    if (this.isLoggedIn()) {
      this.fetchNotifications();
    }
  },
  beforeUnmount() {
    this.$root.$off('dashboard-dates-init', this.onDashboardDatesInit);
    this.$root.$off('dashboard-geo-filters-update', this.onDashboardGeoFiltersUpdate);
    this.$root.$off('form-operations-search-options', this.onFormOperationsSearchOptions);
  },
  methods: {
    ...mapActions('darkMode', ['changeDarkMode']),
    ...mapActions('notification', { fetchNotifications: 'list' }),
    toggleDarkMode() {
      this.changeDarkMode({ isDarkMode: !this.isDarkMode });
    },
    onDashboardDatesInit(payload) {
      if (!payload) return;
      this.dashboardStartDate = payload.startDate || this.dashboardStartDate;
      this.dashboardEndDate = payload.endDate || this.dashboardEndDate;
      this.dashboardMinDate = payload.minDate || this.dashboardMinDate;
      this.dashboardMaxDate = payload.maxDate || this.dashboardMaxDate;
    },
    onDashboardGeoFiltersUpdate(payload) {
      if (!payload || !this.isDashboardRoute) return;
      this.dashboardGeoPrimaryOptions = payload.primaryOptions || [];
      this.dashboardGeoSecondaryOptions = payload.secondaryOptions || [];
      this.dashboardGeoPrimarySelected = payload.primarySelected || 'select';
      this.dashboardGeoSecondarySelected = payload.secondarySelected || 'select';
    },
    onDashboardGeoPrimaryChange(value) {
      this.dashboardGeoPrimarySelected = value;
      this.$root.$emit('dashboard-geo-primary-change', value);
    },
    onDashboardGeoSecondaryChange(value) {
      this.dashboardGeoSecondarySelected = value;
      this.$root.$emit('dashboard-geo-secondary-change', value);
    },
    onOsaReportDownload() {
      this.$root.$emit('osa-report-download');
    },
    onOsaDownloadMis() {
      this.$root.$emit('osa-report-download-mis');
    },
    onFormOperationsSearchOptions(options) {
      if (Array.isArray(options) && options.length) {
        this.formOperationsSearchCategoryOptions = options;
        if (this.isFormOperationsRoute) {
          this.topbarSearchKey = this.resolveFormOperationsSearchKey(this.$route.query.searchkey);
        }
      }
    },
    toFormSearchKey(displayName) {
      if (!displayName) {
        return '';
      }
      let key = displayName.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
      key = key.substr(0, 1).toLowerCase() + key.substr(1);
      return key;
    },
    toSharedListSearchKey(displayName) {
      if (!['transaction-complain', 'ene-tracker', 'rtv'].includes(this.$route.name)) {
        return displayName;
      }
      const keyMap = {
        Name: 'name',
        'Store ID': 'store_id',
        'Store Name': 'store_name',
        'Transaction ID': 'transaction_id',
        'Transaction Date': 'transaction_date'
      };
      return keyMap[displayName] || displayName;
    },
    fromSharedListSearchKey(searchKey) {
      if (!['transaction-complain', 'ene-tracker', 'rtv'].includes(this.$route.name)) {
        return searchKey;
      }
      const labelMap = {
        name: 'Name',
        store_id: 'Store ID',
        store_name: 'Store Name',
        transaction_id: 'Transaction ID',
        transaction_date: 'Transaction Date'
      };
      return labelMap[searchKey] || searchKey;
    },
    resolveFormOperationsSearchKey(searchKey) {
      if (!searchKey) {
        return this.formOperationsSearchCategoryOptions[0] || 'Select Filter';
      }
      const found = this.formOperationsSearchCategoryOptions.find(
        option => this.toFormSearchKey(option) === searchKey
      );
      return found || searchKey;
    },
    onTopbarStartDateChange(value) {
      this.dashboardStartDate = value;
      this.topbarDatesDraft = false;
    },
    onTopbarEndDateChange(value) {
      this.dashboardEndDate = value;
      this.topbarDatesDraft = false;
    },
    onTopbarMonthChange(value) {
      this.topbarSelectedMonth = value;
      this.topbarDatesDraft = false;
    },
    onTopbarYearChange(value) {
      this.topbarSelectedYear = value;
      this.topbarDatesDraft = false;
    },
    // Draft/prefill dates stay UI-only. Only commit dates the user changed,
    // or dates already applied on the route (topbarDatesDraft === false).
    assignDraftAwareDatesToQuery(query, { singleDate = false } = {}) {
      const nextQuery = { ...query };
      if (this.topbarDatesDraft) {
        delete nextQuery.startdate;
        delete nextQuery.enddate;
        return nextQuery;
      }
      if (singleDate) {
        if (this.dashboardStartDate) {
          nextQuery.startdate = this.dashboardStartDate;
          nextQuery.enddate = this.dashboardStartDate;
        } else {
          delete nextQuery.startdate;
          delete nextQuery.enddate;
        }
        return nextQuery;
      }
      if (this.dashboardStartDate) {
        nextQuery.startdate = this.dashboardStartDate;
      } else {
        delete nextQuery.startdate;
      }
      if (this.dashboardEndDate) {
        nextQuery.enddate = this.dashboardEndDate;
      } else {
        delete nextQuery.enddate;
      }
      return nextQuery;
    },
    onDashboardApplyFilter() {
      if (this.isDashboardRoute) {
        this.$root.$emit('dashboard-apply-filter', {
          startDate: this.dashboardStartDate,
          endDate: this.dashboardEndDate
        });
        return;
      }
      if (this.isUserListRoute) {
        const query = { ...this.$route.query };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Name';
          if (query.searchkey === 'Role') {
            query.role = this.topbarSearch;
          } else {
            delete query.role;
          }
        } else {
          delete query.search;
          delete query.searchkey;
          delete query.role;
        }
        this.$router.push({ query });
        return;
      }
      if (this.isFormListRoute) {
        const query = { ...this.$route.query };
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isFormOperationsRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.searchvalue = this.topbarSearch;
          if (this.topbarSearchKey) {
            query.searchkey = this.toFormSearchKey(this.topbarSearchKey);
          }
        } else {
          delete query.searchvalue;
          delete query.searchkey;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isQmsListRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || this.topbarSearchCategoryFallbackLabel;
        } else {
          delete query.search;
          delete query.searchkey;
        }
        if (this.topbarFilterSelected) {
          query.filter = this.topbarFilterSelected;
        } else {
          delete query.filter;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isPettyCashBudgetRoute || this.isPettyCashBudgetIncreaseRoute || this.showPettyCashAccountTopbarFilters) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Store Id';
        } else {
          delete query.search;
          delete query.searchkey;
        }
        if (this.isPettyCashBudgetIncreaseRoute && this.topbarFilterSelected) {
          query.filter = this.topbarFilterSelected;
        } else if (this.isPettyCashBudgetIncreaseRoute) {
          delete query.filter;
        }
        if (this.topbarDatesDraft) {
          delete query.startdate;
          delete query.enddate;
        } else if (this.topbarSelectedMonth && this.topbarSelectedYear) {
          const monthIndex = Months.indexOf(this.topbarSelectedMonth);
          if (monthIndex >= 0) {
            const inputDate = moment()
              .year(Number(this.topbarSelectedYear))
              .month(monthIndex);
            query.startdate = inputDate.startOf('month').format('YYYY-MM-DD');
            query.enddate = inputDate.endOf('month').format('YYYY-MM-DD');
          }
        }
        this.$router.push({ query });
        return;
      }
      if (this.isPettyCashExpenseRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Store Id';
        } else {
          delete query.search;
          delete query.searchkey;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.showPettyCashAccountEditTopbarFilters) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarFilterSelected) {
          query.filter = this.topbarFilterSelected;
        } else {
          delete query.filter;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isPopRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Store Id';
        } else {
          delete query.search;
          delete query.searchkey;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query, { singleDate: true }) });
        return;
      }
      if (this.isCxPulse360Route) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Store Id';
        } else {
          delete query.search;
          delete query.searchkey;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isSamparkRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.topbarSearchKey || 'Customer Name';
        } else {
          delete query.search;
          delete query.searchkey;
        }
        this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
        return;
      }
      if (this.isSharedListTopbarRoute || this.isAdditionalListTopbarRoute || this.isBarcodeScanRoute) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          query.searchkey = this.toSharedListSearchKey(this.topbarSearchKey || this.topbarSearchCategoryFallbackLabel);
        } else {
          delete query.search;
          delete query.searchkey;
        }
        if (this.topbarFilterSelected) {
          query.filter = this.topbarFilterSelected;
        } else {
          delete query.filter;
        }
        if (this.topbarDatesDraft) {
          // UI-only prefill/clear: do not push draft dates. Keep an existing
          // non-today route window (e.g. Transaction Complain last-month default).
          const today = moment().format('YYYY-MM-DD');
          const routeStart = this.$route.query.startdate;
          const routeEnd = this.$route.query.enddate;
          if (routeStart && routeEnd && (routeStart !== today || routeEnd !== today)) {
            query.startdate = routeStart;
            query.enddate = routeEnd;
          } else if (this.$route.name === 'transaction-complain' && !routeStart && !routeEnd) {
            query.startdate = moment().subtract(1, 'months').format('YYYY-MM-DD');
            query.enddate = today;
          } else {
            delete query.startdate;
            delete query.enddate;
          }
        } else {
          if (this.dashboardStartDate) {
            query.startdate = this.dashboardStartDate;
          } else {
            delete query.startdate;
          }
          if (this.dashboardEndDate) {
            query.enddate = this.dashboardEndDate;
          } else {
            delete query.enddate;
          }
        }
        this.$router.push({ query });
        return;
      }
      if (
        this.isChecklistRoute ||
        this.isReportRoute ||
        this.isEventsRoute ||
        this.isEventPlanListRoute ||
        this.isFoodSafetyReportsRoute ||
        this.isNpsVendorRoute ||
        this.isNpsVocRoute ||
        this.isComplaintTrackerRoute
      ) {
        const query = { ...this.$route.query, page: 1 };
        if (this.topbarSearch) {
          query.search = this.topbarSearch;
          if (this.isEventsRoute || this.isNpsVendorRoute) {
            query.searchkey =
              this.topbarSearchKey ||
              (this.isNpsVendorRoute ? 'Vendor Code' : 'Store Id');
          } else if (this.isReportRoute || this.isFoodSafetyReportsRoute) {
            query.searchkey = this.topbarSearchKey || 'CheckList Name';
          } else if (this.isChecklistListRoute || this.isEventPlanListRoute) {
            query.searchkey = this.topbarSearchKey || 'Name';
          } else if (this.isNpsVocRoute) {
            query.searchkey = this.topbarSearchKey || 'Mobile';
          } else if (this.isComplaintTrackerRoute) {
            query.searchkey = this.topbarSearchKey || 'Complain Number';
          }
        } else {
          delete query.search;
          if (
            this.isEventsRoute ||
            this.isNpsVendorRoute ||
            this.isNpsVocRoute ||
            this.isReportRoute ||
            this.isFoodSafetyReportsRoute ||
            this.isChecklistListRoute ||
            this.isEventPlanListRoute ||
            this.isComplaintTrackerRoute
          ) {
            delete query.searchkey;
          }
        }
        // Vendor report uses intentional default dates (not a "today" draft prefill).
        if (this.isNpsVendorRoute) {
          if (this.dashboardStartDate) {
            query.startdate = this.dashboardStartDate;
          } else {
            delete query.startdate;
          }
          if (this.dashboardEndDate) {
            query.enddate = this.dashboardEndDate;
          } else {
            delete query.enddate;
          }
        } else {
          this.$router.push({ query: this.assignDraftAwareDatesToQuery(query) });
          return;
        }
        this.$router.push({ query });
      }
    },
    onTopbarClearDates() {
      if (!this.showTopbarClearButton) {
        return;
      }

      if (this.isUserListRoute) {
        this.topbarSearch = '';
        this.topbarSearchKey = 'Name';
        const query = { ...this.$route.query };
        delete query.search;
        delete query.searchkey;
        this.$router.push({ query });
        return;
      }

      // Clear dates only (UI-only) — search stays; no API until Apply Filter.
      if (this.isPettyCashBudgetRoute || this.isPettyCashBudgetIncreaseRoute || this.showPettyCashAccountTopbarFilters) {
        this.topbarSelectedMonth = moment().format('MMMM').toUpperCase();
        this.topbarSelectedYear = new Date().getFullYear();
        this.topbarDatesDraft = true;
        return;
      }

      if (this.isPettyCashExpenseRoute || this.showPettyCashAccountEditTopbarFilters) {
        this.dashboardStartDate = '';
        this.dashboardEndDate = '';
        this.topbarDatesDraft = true;
        return;
      }

      this.dashboardStartDate = '';
      this.dashboardEndDate = '';
      this.topbarDatesDraft = true;
    },
    onTopbarSearchCategoryChange(value) {
      this.topbarSearchKey = value || this.topbarSearchCategoryFallbackLabel;
    },
    onTopbarSearchInput(value) {
      this.topbarSearch = value;
      // Clearing the search box removes the active search filter and reloads the list.
      if (
        (this.isUserListRoute || this.isSamparkRoute || this.isSharedListTopbarRoute) &&
        value === '' &&
        Object.prototype.hasOwnProperty.call(this.$route.query, 'search')
      ) {
        const query = { ...this.$route.query };
        delete query.search;
        delete query.searchkey;
        this.$router.push({ query });
      }
    },
    onTopbarFilterChange(value) {
      if (this.isEventsRoute || this.isNpsVendorRoute) {
        this.topbarSearchKey =
          value || (this.isNpsVendorRoute ? 'Vendor Code' : 'Store Id');
        return;
      }

      this.topbarFilterSelected = value;

      if (this.showPettyCashAccountTopbarFilters || this.showPettyCashAccountEditTopbarFilters) {
        const query = { ...this.$route.query, page: 1 };
        if (value) {
          query.filter = value;
        } else {
          delete query.filter;
        }
        this.$router.push({ query });
        return;
      }

      if (!this.isReportRoute && !this.isFoodSafetyReportsRoute && !this.isChecklistListRoute && !this.isQmsListRoute) {
        if (!this.isSharedListTopbarRoute && !this.isAdditionalListTopbarRoute && !this.isBarcodeScanRoute) {
          return;
        }
      }

      const query = { ...this.$route.query, page: 1 };
      if (value) {
        query.filter = value;
      } else {
        delete query.filter;
      }
      this.$router.push({ query });
    },
    syncTopbarDatesFromQuery(query) {
      const today = moment().format('YYYY-MM-DD');

      if (this.isNpsVendorRoute) {
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
        } else if (Object.prototype.hasOwnProperty.call(query, 'startdate')) {
          this.dashboardStartDate = '';
        } else {
          this.dashboardStartDate = moment().subtract(2, 'month').format('YYYY-MM-DD');
        }
        if (query.enddate) {
          this.dashboardEndDate = query.enddate;
        } else if (Object.prototype.hasOwnProperty.call(query, 'enddate')) {
          this.dashboardEndDate = '';
        } else {
          this.dashboardEndDate = today;
        }
        // Vendor defaults are intentional applied defaults, not UI-only prefills.
        this.topbarDatesDraft = false;
        return;
      }
      if (this.isFormListRoute || this.isFormOperationsRoute) {
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
        } else if (Object.prototype.hasOwnProperty.call(query, 'startdate')) {
          this.dashboardStartDate = '';
        } else {
          this.dashboardStartDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        }
        if (query.enddate) {
          this.dashboardEndDate = query.enddate;
        } else if (Object.prototype.hasOwnProperty.call(query, 'enddate')) {
          this.dashboardEndDate = '';
        } else {
          this.dashboardEndDate = today;
        }
        this.topbarDatesDraft = false;
        return;
      }
      if (this.isComplaintTrackerRoute) {
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
        } else if (this.showTopbarClearButton) {
          this.dashboardStartDate = today;
        } else {
          this.dashboardStartDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        }
        if (query.enddate) {
          this.dashboardEndDate = query.enddate;
        } else if (this.showTopbarClearButton) {
          this.dashboardEndDate = today;
        } else {
          this.dashboardEndDate = today;
        }
        this.topbarDatesDraft = !(query.startdate || query.enddate);
        return;
      }
      if (this.isPopRoute) {
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
          this.dashboardEndDate = query.startdate;
        } else {
          this.dashboardStartDate = today;
          this.dashboardEndDate = today;
        }
        this.topbarDatesDraft = !query.startdate;
        return;
      }
      if (query.startdate) {
        this.dashboardStartDate = query.startdate;
      } else if (this.isTopbarCurrentDatePrefillRoute) {
        this.dashboardStartDate = today;
      } else if (this.showTopbarClearButton) {
        this.dashboardStartDate = '';
      } else if (this.$route.name === 'fill-checklist') {
        this.dashboardStartDate = today;
      } else if (this.$route.name === 'vendor-feedback-report') {
        this.dashboardStartDate = moment().subtract(2, 'month').format('YYYY-MM-DD');
      }
      if (query.enddate) {
        this.dashboardEndDate = query.enddate;
      } else if (this.isTopbarCurrentDatePrefillRoute) {
        this.dashboardEndDate = today;
      } else if (this.showTopbarClearButton) {
        this.dashboardEndDate = '';
      } else if (this.$route.name === 'fill-checklist') {
        this.dashboardEndDate = today;
      } else if (this.$route.name === 'vendor-feedback-report') {
        this.dashboardEndDate = today;
      }
      this.topbarDatesDraft = !(query.startdate || query.enddate);
    },
    syncTopbarFromRoute() {
      if (this.isUserListRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || query.role || '';
        if (['Name', 'Mobile', 'Role'].includes(query.searchkey)) {
          this.topbarSearchKey = query.searchkey;
        } else {
          this.topbarSearchKey = query.role ? 'Role' : 'Name';
        }
        return;
      }
      if (this.isEventsRoute || this.isNpsVendorRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || (this.isNpsVendorRoute ? 'Vendor Code' : 'Store Id');
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isEventPlanListRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Name';
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isPettyCashBudgetRoute || this.isPettyCashBudgetIncreaseRoute || this.showPettyCashAccountTopbarFilters) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Store Id';
        if (this.isPettyCashBudgetIncreaseRoute) {
          this.topbarFilterSelected = query.filter || null;
        }
        if (query.startdate) {
          const parsed = moment(query.startdate, 'YYYY-MM-DD', true);
          if (parsed.isValid()) {
            this.topbarSelectedMonth = parsed.format('MMMM').toUpperCase();
            this.topbarSelectedYear = parsed.year();
          }
        } else {
          this.topbarSelectedMonth = moment().format('MMMM').toUpperCase();
          this.topbarSelectedYear = new Date().getFullYear();
        }
        if (this.showPettyCashAccountTopbarFilters) {
          if (Object.prototype.hasOwnProperty.call(query, 'filter')) {
            this.topbarFilterSelected = query.filter;
          } else {
            this.topbarFilterSelected = null;
          }
        }
        this.topbarDatesDraft = !query.startdate;
        return;
      }

      if (this.isPettyCashExpenseRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Store Id';
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
        } else {
          this.dashboardStartDate = '';
        }
        if (query.enddate) {
          this.dashboardEndDate = query.enddate;
        } else {
          this.dashboardEndDate = '';
        }
        this.topbarDatesDraft = !(query.startdate || query.enddate);
        return;
      }

      if (this.showPettyCashAccountEditTopbarFilters) {
        const query = this.$route.query || {};
        if (Object.prototype.hasOwnProperty.call(query, 'filter')) {
          this.topbarFilterSelected = query.filter;
        } else {
          this.topbarFilterSelected = null;
        }
        if (query.startdate) {
          this.dashboardStartDate = query.startdate;
        } else {
          this.dashboardStartDate = '';
        }
        if (query.enddate) {
          this.dashboardEndDate = query.enddate;
        } else {
          this.dashboardEndDate = '';
        }
        this.topbarDatesDraft = !(query.startdate || query.enddate);
        return;
      }

      if (this.isComplaintTrackerRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Complain Number';
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isPopRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Store Id';
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isCxPulse360Route) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || 'Store Id';
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isSamparkRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = ['Customer Name', 'Mobile'].includes(query.searchkey)
          ? query.searchkey
          : 'Customer Name';
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isSharedListTopbarRoute || this.isAdditionalListTopbarRoute || this.isBarcodeScanRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = this.fromSharedListSearchKey(query.searchkey || this.topbarSearchCategoryFallbackLabel);
        this.topbarFilterSelected = query.filter || null;
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isFormListRoute) {
        this.topbarSearch = '';
        this.syncTopbarDatesFromQuery(this.$route.query || {});
        return;
      }

      if (this.isFormOperationsRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.searchvalue || '';
        this.topbarSearchKey = this.resolveFormOperationsSearchKey(query.searchkey);
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (this.isQmsListRoute) {
        const query = this.$route.query || {};
        this.topbarSearch = query.search || '';
        this.topbarSearchKey = query.searchkey || this.topbarSearchCategoryFallbackLabel;
        this.topbarFilterSelected = query.filter || null;
        this.syncTopbarDatesFromQuery(query);
        return;
      }

      if (!this.isChecklistRoute && !this.isReportRoute && !this.isFoodSafetyReportsRoute && !this.isNpsVocRoute && !this.isFormOperationsRoute) {
        if (this.isDashboardRoute) {
          this.syncTopbarDatesFromQuery(this.$route.query || {});
          return;
        }
        if (!this.isDashboardRoute) {
          this.topbarSearch = '';
        }
        if (!this.isDashboardRoute) {
          this.topbarFilterSelected = null;
          this.topbarSearchKey = 'Store Id';
        }
        return;
      }
      const query = this.$route.query || {};
      this.topbarSearch = query.search || '';
      if (this.isReportRoute || this.isFoodSafetyReportsRoute) {
        this.topbarSearchKey = query.searchkey || 'CheckList Name';
      } else if (this.isChecklistListRoute) {
        this.topbarSearchKey = query.searchkey || 'Name';
      } else if (this.isNpsVocRoute) {
        this.topbarSearchKey = query.searchkey || 'Mobile';
      }
      if (Object.prototype.hasOwnProperty.call(query, 'filter')) {
        this.topbarFilterSelected = query.filter;
      } else {
        this.topbarFilterSelected = null;
      }
      this.syncTopbarDatesFromQuery(query);
    }
  },
  components: {
    AppSidebar,
    AdminTopbar,
    FooterBar
  }
};
</script>

<style scoped>
.app-layout-backend {
  min-height: 100vh;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  background: var(--admin-bg, #fff);
}

.app-layout-backend--full {
  height: 100vh;
  max-height: 100vh;
}

.app-layout-backend__container--full {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.admin-page-shell--full {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.backend-main {
  min-height: 0;
  height: 100%;
  max-height: 100vh;
  overflow: hidden;
  min-width: 0;
}

.admin-topbar-shell {
  flex-shrink: 0;
  position: relative;
  z-index: 1020;
  background: #fff;
  border-bottom: 1px solid #e8edf4;
  overflow: visible;
}

.backend-main--sidebar-collapsed .admin-topbar-shell {
  margin-left: 124px;
}

.admin-topbar-shell-dark {
  background: #172033;
  border-color: #283548;
}

.backend-content {
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0;
  background: var(--admin-bg, #fff);
}

.backend-content-dark {
  background: #111827;
}

.admin-page-shell {
  min-height: calc(100vh - 110px);
}
</style>
